import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, Edit, Eye, EyeOff, FileText, BookOpen, Plus, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const CatalogsManager = () => {
  const [catalogs, setCatalogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);

  const [editData, setEditData] = useState({
    title: '',
    description: ''
  });

  useEffect(() => {
    loadCatalogs();
  }, []);

  const loadCatalogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const base = process.env.REACT_APP_BACKEND_URL;

      // Lade beide Quellen parallel: hochgeladene Kataloge + selbst-erstellte Produktkataloge
      const [uploadedRes, generatedRes] = await Promise.allSettled([
        fetch(`${base}/api/catalogs/`, { headers }),
        fetch(`${base}/api/catalogs/product-catalogs`, { headers }),
      ]);

      const uploaded = uploadedRes.status === 'fulfilled' && uploadedRes.value.ok
        ? await uploadedRes.value.json()
        : [];
      const generated = generatedRes.status === 'fulfilled' && generatedRes.value.ok
        ? await generatedRes.value.json()
        : [];

      // Markiere die Quelle, damit die UI passend reagiert
      const merged = [
        ...uploaded.map((c) => ({ ...c, _source: 'uploaded' })),
        ...generated.map((c) => ({ ...c, _source: 'generated' })),
      ].sort((a, b) => {
        const ta = new Date(a.updated_at || a.created_at || 0).getTime();
        const tb = new Date(b.updated_at || b.created_at || 0).getTime();
        return tb - ta;
      });
      setCatalogs(merged);
    } catch (error) {
      console.error('Error loading catalogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (catalogId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/catalogs/${catalogId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(editData)
      });
      
      if (response.ok) {
        toast.success('Katalog aktualisiert!');
        setEditingId(null);
        loadCatalogs();
      }
    } catch (error) {
      console.error('Error updating:', error);
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleDelete = async (catalogId, source) => {
    if (!confirm('Katalog wirklich löschen?')) return;
    
    try {
      const endpoint = source === 'generated'
        ? `/api/catalogs/product-catalogs/${catalogId}`
        : `/api/catalogs/${catalogId}`;
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        toast.success('Katalog gelöscht!');
        loadCatalogs();
      } else {
        toast.error('Löschen fehlgeschlagen');
      }
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const handleToggle = async (catalogId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/catalogs/${catalogId}/toggle`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        loadCatalogs();
      }
    } catch (error) {
      console.error('Error toggling:', error);
      toast.error('Fehler beim Umschalten');
    }
  };

  if (loading) {
    return <div className="p-8">Lade Kataloge...</div>;
  }

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto" style={{ backgroundColor: '#F5EDE4', minHeight: '100vh' }}>
      {/* Goldbraune Haupt-Überschrift */}
      <div className="mb-4 p-3 md:p-4 flex items-center gap-3" style={{ backgroundColor: '#C4A084' }}>
        <div className="w-10 h-10 rounded flex items-center justify-center bg-white/90">
          <BookOpen className="h-5 w-5" style={{ color: '#C4A084' }} />
        </div>
        <div>
          <h1 className="text-lg md:text-xl font-bold text-white">Produktkataloge</h1>
          <p className="text-xs md:text-sm text-white/80">Verwalten Sie Ihre Kataloge</p>
        </div>
      </div>
      
      {/* CTA: Neuen Katalog erstellen → Generator */}
      <Card className="mb-6 border-[#C4A084] bg-gradient-to-br from-[#FAF5EE] to-white">
        <CardContent className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="w-11 h-11 bg-[#C4A084] flex items-center justify-center shrink-0">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-semibold text-[#5D4E3C]" style={{ fontFamily: 'Georgia, serif' }}>
                Neuen Katalog erstellen
              </h2>
              <p className="text-xs sm:text-sm text-[#8B7355]">
                Produkte aus deinem Sortiment auswählen, kuratieren &amp; als PDF speichern. Bleibt persistent in der Liste unten.
              </p>
            </div>
          </div>
          <Link to="/admin/product-catalog-generator" className="w-full sm:w-auto">
            <Button
              className="w-full sm:w-auto rounded-none bg-[#5D4E3C] hover:bg-[#3E2723] text-white whitespace-nowrap"
              data-testid="open-catalog-generator-btn"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Katalog erstellen
              <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
            </Button>
          </Link>
        </CardContent>
      </Card>

      {/* Catalogs List - Goldbraune Überschrift */}
      <div className="mb-3 p-2 px-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
        <div className="w-6 h-6 rounded flex items-center justify-center bg-[#FFFEF5]">
          <FileText className="h-4 w-4 text-[#D4B030]" />
        </div>
        <h2 className="text-base font-semibold text-white">Vorhandene Kataloge ({catalogs.length})</h2>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {catalogs.map((catalog) => (
          <Card key={catalog.id} className={`border-[#C4A084] ${!catalog.is_active ? 'opacity-60' : ''}`}>
            <CardHeader className="p-0">
              <div className="p-3 flex items-center justify-between" style={{ backgroundColor: catalog.is_active ? '#C4A084' : '#E8DFD4' }}>
                <CardTitle className="text-white text-sm truncate">{catalog.title}</CardTitle>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggle(catalog.id)}
                    className="text-white hover:bg-white/20 h-7 w-7 p-0"
                  >
                    {catalog.is_active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              {editingId === catalog.id ? (
                <div className="space-y-3">
                  <Input
                    value={editData.title}
                    onChange={(e) => setEditData({...editData, title: e.target.value})}
                    placeholder="Titel"
                    className="border-[#C4A084]"
                  />
                  <Textarea
                    value={editData.description}
                    onChange={(e) => setEditData({...editData, description: e.target.value})}
                    placeholder="Beschreibung"
                    rows={2}
                    className="border-[#C4A084]"
                  />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => handleUpdate(catalog.id)} className="bg-[#C4A084] hover:bg-[#A08060] text-white">
                      Speichern
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditingId(null)} className="border-[#C4A084] text-[#5D4E3C]">
                      Abbrechen
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between mb-3 gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded flex items-center justify-center shrink-0" style={{ background: catalog._source === 'generated' ? '#F0FFF4' : '#F8F0FF' }}>
                        {catalog._source === 'generated' ? (
                          <Sparkles className="h-5 w-5" style={{ color: '#60A070' }} />
                        ) : (
                          <FileText className="h-5 w-5 text-[#A070D0]" />
                        )}
                      </div>
                      <span
                        className="text-[9px] tracking-wider uppercase px-1.5 py-0.5 border whitespace-nowrap"
                        style={catalog._source === 'generated'
                          ? { color: '#3E7C50', borderColor: '#C5E0CB', background: '#F0FFF4' }
                          : { color: '#5D4E3C', borderColor: '#E8DCC8', background: '#FAF5EE' }}
                        data-testid={`catalog-source-${catalog.id}`}
                      >
                        {catalog._source === 'generated' ? '✨ Erstellt' : '⤴ Hochgeladen'}
                      </span>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      {catalog._source !== 'generated' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditingId(catalog.id);
                            setEditData({
                              title: catalog.title,
                              description: catalog.description || ''
                            });
                          }}
                          className="h-8 w-8 p-0 bg-[#F0F8FF] hover:bg-[#E0F0FF] text-[#60A0D0]"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(catalog.id, catalog._source)}
                        className="h-8 w-8 p-0 bg-[#FFF0F0] hover:bg-[#FFE0E0] text-[#D06060]"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <h3 className="font-bold mb-1 text-[#5D4E3C] break-words">{catalog.title || catalog.name || 'Katalog'}</h3>
                  {catalog.description && (
                    <p className="text-sm text-[#8B7355] mb-2 break-words">{catalog.description}</p>
                  )}
                  <div className="text-xs text-[#A09080]">
                    {catalog._source === 'generated' ? (
                      <>
                        {catalog.product_count ? `${catalog.product_count} Produkte` : 'Eigener Produktkatalog'}
                        {catalog.created_at && ` • ${new Date(catalog.created_at).toLocaleDateString('de-DE')}`}
                      </>
                    ) : (
                      <>
                        <span className="uppercase">{catalog.file_type}</span> • {((catalog.file_size || 0) / 1024).toFixed(0)} KB
                      </>
                    )}
                  </div>
                  {catalog._source === 'generated' ? (
                    <Link
                      to="/admin/product-catalog-generator"
                      className="text-sm text-[#C4A084] hover:text-[#A08060] hover:underline mt-2 inline-block font-medium"
                      data-testid={`catalog-open-generator-${catalog.id}`}
                    >
                      Im Generator öffnen →
                    </Link>
                  ) : (catalog.file_url && (
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}${catalog.file_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-[#C4A084] hover:text-[#A08060] hover:underline mt-2 inline-block font-medium"
                      data-testid={`catalog-download-${catalog.id}`}
                    >
                      Vorschau öffnen →
                    </a>
                  ))}
                </>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
      
      {catalogs.length === 0 && (
        <Card className="border-[#C4A084]">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded flex items-center justify-center bg-[#F8F0FF]">
              <FileText className="h-8 w-8 text-[#A070D0]" />
            </div>
            <p className="text-[#8B7355]">Noch keine Kataloge vorhanden — erstelle deinen ersten über den Button oben ✨</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CatalogsManager;
